import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, XCircle, Clock, ShieldCheck, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: { color: "text-amber-700 bg-amber-100", icon: Clock, label: "Pending review" },
  approved: { color: "text-green-700 bg-green-100", icon: CheckCircle2, label: "Approved" },
  rejected: { color: "text-rose-700 bg-rose-100", icon: XCircle, label: "Rejected" },
};

export default function AdminKYC() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [reasonMap, setReasonMap] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/kyc/pending`)
      .then(({ data }) => setItems(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const decide = async (u, decision) => {
    try {
      await axios.post(`${API}/admin/kyc/decide`, {
        user_id: u.user_id, decision, reason: reasonMap[u.user_id] || null,
      });
      toast.success(`User ${decision}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!user) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-slate-500">Please sign in.</div>;
  if (!user.is_admin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <XCircle className="mx-auto h-12 w-12 text-rose-500" />
        <h1 className="mt-4 font-display text-2xl font-bold">Admin only</h1>
        <p className="mt-2 text-slate-600">You don&apos;t have access to this page.</p>
      </div>
    );
  }

  const filtered = items.filter((i) => i.kyc_status === filter);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Identity verification queue</h1>
          <p className="mt-1 text-slate-600">Review AI-scored KYC submissions and approve or reject.</p>
        </div>
      </div>

      <div className="mt-6 flex gap-2 border-b border-slate-200">
        {["pending", "approved", "rejected"].map((f) => (
          <button key={f} data-testid={`kyc-filter-${f}`} onClick={() => setFilter(f)}
            className={`border-b-2 px-4 py-2 text-sm font-semibold capitalize transition ${filter === f ? "border-copper text-copper" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {f} · {items.filter((i) => i.kyc_status === f).length}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 rounded-3xl bg-white p-10 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 rounded-3xl bg-white p-10 text-center text-slate-500">No {filter} submissions.</div>
      ) : (
        <div className="mt-6 space-y-4">
          {filtered.map((u) => {
            const S = STATUS_STYLES[u.kyc_status] || STATUS_STYLES.pending;
            return (
              <div key={u.user_id} data-testid={`kyc-row-${u.user_id}`} className="rounded-3xl bg-white p-6 card-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {u.picture ? <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-copper/10" />}
                    <div>
                      <div className="font-display text-lg font-bold">{u.name}</div>
                      <div className="text-sm text-slate-500">{u.email}</div>
                      <div className="mt-1 text-xs text-slate-500">Document: <span className="font-medium capitalize">{u.kyc_document_type?.replace("_", " ")}</span></div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${S.color}`}>
                    <S.icon className="h-3.5 w-3.5" /> {S.label}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">ID document</div>
                    <div className="mt-1 aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
                      <img src={`${API}/kyc/file/${u.kyc_document_path}`} alt="doc" className="h-full w-full object-cover" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Selfie</div>
                    <div className="mt-1 aspect-[4/3] overflow-hidden rounded-xl bg-slate-100">
                      <img src={`${API}/kyc/file/${u.kyc_selfie_path}`} alt="selfie" className="h-full w-full object-cover" />
                    </div>
                  </div>
                </div>

                {u.kyc_ai_verdict && (
                  <div className="mt-4 rounded-2xl bg-copper/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-copper">
                      <Sparkles className="h-3.5 w-3.5" /> AI verdict · score {u.kyc_ai_verdict.match_score}/100 · {u.kyc_ai_verdict.recommendation}
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{u.kyc_ai_verdict.notes}</p>
                  </div>
                )}

                {u.kyc_status === "pending" && (
                  <div className="mt-4">
                    <input
                      data-testid={`reason-${u.user_id}`}
                      value={reasonMap[u.user_id] || ""}
                      onChange={(e) => setReasonMap({ ...reasonMap, [u.user_id]: e.target.value })}
                      placeholder="Optional reason for guest (shown on rejection)"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
                    />
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => decide(u, "approved")} data-testid={`approve-${u.user_id}`} className="btn-primary text-sm">
                        <CheckCircle2 className="mr-1 inline h-4 w-4" /> Approve
                      </button>
                      <button onClick={() => decide(u, "rejected")} data-testid={`reject-${u.user_id}`} className="btn-outline text-sm">
                        <XCircle className="mr-1 inline h-4 w-4" /> Reject
                      </button>
                    </div>
                  </div>
                )}

                {u.kyc_reason && u.kyc_status === "rejected" && (
                  <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">Reason: {u.kyc_reason}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
